// /pages/HR/HRDashboard.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Import all dashboard components
import KPIGrid from "./components/kpis/KPIGrid.js";
import ChartCard from "./components/charts/ChartCard.js";
import BarChart from "./components/charts/BarChart.js";
import PieChart from "./components/charts/PieChart.js";
import ComboChart from "./components/charts/ComboChart.js";
import FilterBar from "./components/filters/FilterBar.js";

// Import utilities
import { hrDataService } from "../HR/components/utils/hrDataService.js";
import { CHART_COLORS } from "../HR/components/utils/hrConstants.js";

// Import styles
import "../HR/components/styles/HRDashboard.css";

function HRDashboard() {
  const navigate = useNavigate();

  // State management
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: { startDate: null, endDate: null, preset: "last30days" },
    locations: [],
    categories: [],
    status: "all",
    search: "",
  });

  // Fetch dashboard data - NOT using useCallback to avoid infinite loop
  const fetchDashboardData = async (currentFilters) => {
    try {
      setLoading(true);
      const data = await hrDataService.fetchDashboardData(currentFilters || filters);
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Initial data load - only run once on mount
  useEffect(() => {
    const loadInitialData = async () => {
      const savedFilters = hrDataService.loadFilterPreferences();
      const initialFilters = savedFilters || {
        dateRange: { startDate: null, endDate: null, preset: "last30days" },
        locations: [],
        categories: [],
        status: "all",
        search: "",
      };
      
      if (savedFilters) {
        setFilters(savedFilters);
      }
      
      try {
        setLoading(true);
        const data = await hrDataService.fetchDashboardData(initialFilters);
        setDashboardData(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, []); // Empty dependency array - only runs once

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    hrDataService.saveFilterPreferences(newFilters);
    fetchDashboardData(newFilters);
  };

  // Handle KPI clicks
  const handleKPIClick = (kpiType) => {
    const queryParams = new URLSearchParams();

    switch (kpiType) {
      case "total":
        navigate("/hr/requests");
        break;
      case "completion":
        queryParams.append("status", "completed");
        navigate(`/hr/requests?${queryParams.toString()}`);
        break;
      case "avgDays":
        queryParams.append("sort", "duration");
        navigate(`/hr/requests?${queryParams.toString()}`);
        break;
      default:
        navigate("/hr/requests");
    }
  };

  // Handle chart data point clicks
  const handleChartClick = (chartType, data) => {
    console.log("Chart clicked:", chartType, data);

    if (chartType === "category" && data.label) {
      navigate(`/hr/requests?category=${encodeURIComponent(data.label)}`);
    } else if (chartType === "location" && data.label) {
      navigate(`/hr/requests?location=${data.label}`);
    }
  };

  // Handle fullscreen toggle for charts
  const handleFullscreen = (chartId, isFullscreen) => {
    console.log(`Chart ${chartId} fullscreen: ${isFullscreen}`);
  };

  // Handle export
  const handleExport = async (format) => {
    try {
      await hrDataService.exportDashboardData(format, dashboardData);
      console.log(`Dashboard exported as ${format}`);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  // Loading state
  if (loading && !dashboardData) {
    return (
      <div className="hr-dashboard-container">
        <div className="hr-dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !dashboardData) {
    return (
      <div className="hr-dashboard-container">
        <div className="hr-dashboard-error">
          <p className="error-message">{error}</p>
          <button onClick={() => fetchDashboardData(filters)} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Create weekly bar colors - matching your image
  const getWeeklyBarColors = () => {
    const colors = [];
    const data = dashboardData?.weeklyData || [];
    data.forEach((item, index) => {
      // W20 is dark blue, others are yellow
      if (item.week === 'W20' || item.week === 'W26') {
        colors.push(CHART_COLORS.weeklyBarColorDark);
      } else {
        colors.push(CHART_COLORS.weeklyBarColor);
      }
    });
    return colors;
  };

  return (
    <div className="hr-dashboard-container">
      {/* Single header with title and filters on same line */}
      <div className="hr-dashboard-header">
        <h1 className="hr-dashboard-title">HR Dashboard</h1>
        <div className="hr-dashboard-filters">
          <FilterBar
            onFilterChange={handleFilterChange}
            defaultFilters={filters}
            showDateRange={true}
            showLocation={true}
            showCategory={true}
            showStatus={true}
            showSearch={false}
            inline={true}
            compact={true}
          />
        </div>
      </div>

      <div className="hr-dashboard-content">
        {/* KPI Section */}
        <div className="dashboard-kpis">
          <KPIGrid
            data={{
              kpis: dashboardData?.kpis || {},
              locations: dashboardData?.locationMetrics || [],
            }}
            loading={false}
            onKPIClick={handleKPIClick}
            showLocations={true}
            showLocationHeader={false}
            refreshInterval={null}
          />
        </div>

        {/* Charts Grid */}
        <div className="dashboard-charts-grid">
          {/* Category Breakdown Chart */}
          <ChartCard
            title="Request Category Breakdown"
            className="chart-category"
            loading={false}
            onExport={(format) => handleExport(format)}
            onFullscreen={(isFullscreen) =>
              handleFullscreen("category", isFullscreen)
            }
            showRefreshButton={false}
            showExportButton={false}
            showFullscreenButton={false}
            minHeight={350}
          >
            <BarChart
              data={
                dashboardData?.categoryBreakdown?.map((item) => item.count) ||
                []
              }
              labels={
                dashboardData?.categoryBreakdown?.map(
                  (item) => item.category
                ) || []
              }
              title="Requests"
              yAxisLabel="Number of Requests"
              horizontal={false}
              sortBy={null}
              colors={CHART_COLORS.categoryGradient}
              onDataPointClick={(data) => handleChartClick("category", data)}
              animate={false}
              showValues={true}
              height={300}
            />
          </ChartCard>

          {/* Generalist Performance Chart */}
          <ChartCard
            title="# of Tickets by HR Generalist"
            className="chart-generalist"
            loading={false}
            onExport={(format) => handleExport(format)}
            onFullscreen={(isFullscreen) =>
              handleFullscreen("generalist", isFullscreen)
            }
            showRefreshButton={false}
            showExportButton={false}
            showFullscreenButton={false}
            minHeight={350}
          >
            <BarChart
              data={
                dashboardData?.generalistData?.map((item) => item.completed) ||
                []
              }
              labels={
                dashboardData?.generalistData?.map((item) => item.name) || []
              }
              title="Completed"
              xAxisLabel="Completed Requests"
              yAxisLabel=""
              horizontal={true}
              sortBy={null}
              colors={[CHART_COLORS.generalistBars]}
              onDataPointClick={(data) => handleChartClick("generalist", data)}
              animate={false}
              showValues={true}
              height={300}
            />
          </ChartCard>

          {/* Weekly Trends Chart */}
          <ChartCard
            title="Weekly Trends"
            className="chart-weekly"
            loading={false}
            onExport={(format) => handleExport(format)}
            onFullscreen={(isFullscreen) =>
              handleFullscreen("weekly", isFullscreen)
            }
            showRefreshButton={false}
            showExportButton={false}
            showFullscreenButton={false}
            minHeight={350}
          >
            <ComboChart
              barData={
                dashboardData?.weeklyData?.map((item) => item.created) || []
              }
              lineData={
                dashboardData?.weeklyData?.map((item) => item.avgDaysOpen) || []
              }
              labels={dashboardData?.weeklyData?.map((item) => item.week) || []}
              barLabel="Created"
              lineLabel="Avg Days Open"
              yAxisLabel="Number of Requests"
              y2AxisLabel="Days"
              barColors={getWeeklyBarColors()}
              lineColor={CHART_COLORS.weeklyLineColor}
              showLegend={true}
              showDataPoints={true}
              animate={false}
              height={300}
            />
          </ChartCard>

          {/* Market Distribution Chart */}
          <ChartCard
            title="Market Distribution"
            className="chart-market"
            loading={false}
            onExport={(format) => handleExport(format)}
            onFullscreen={(isFullscreen) =>
              handleFullscreen("market", isFullscreen)
            }
            showRefreshButton={false}
            showExportButton={false}
            showFullscreenButton={false}
            minHeight={350}
          >
            <PieChart
              data={
                dashboardData?.marketDistribution?.map((item) => item.count) ||
                []
              }
              labels={
                dashboardData?.marketDistribution?.map((item) => item.market) ||
                []
              }
              title="Market Share"
              type="doughnut"
              cutout="40%"
              showLegend={true}
              legendPosition="right"
              showPercentage={true}
              colors={CHART_COLORS.pieColors}
              onSegmentClick={(data) => handleChartClick("location", data)}
              animate={false}
              size={280}
            />
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

export default HRDashboard;